import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Title, Text, TextInput, Select, SelectItem, Badge, Flex } from '@tremor/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { getMachinesWithMetrics } from '@/api/machines'
import { useWebSocket } from '@/context/WebSocketContext'
import { OperatorCard } from '@/components/OperatorCard'
import type { MachineWithMetrics } from '@/types'

export function Realtime() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [projectFilter, setProjectFilter] = useState<string>('all')

  // WebSocket real-time data from global context
  const { machines: wsMachines, isConnected: wsConnected, voipStates } = useWebSocket()

  // Helper to get VoIP state from machine_id (format: operator-{extension})
  const getVoipStateForMachine = (machineId: string) => {
    // machine_id format is "operator-{extension}", e.g., "operator-101005"
    const extension = machineId.replace('operator-', '')
    return voipStates[extension] || null
  }

  // Fallback to polling if WebSocket fails
  const { data: apiMachines = [], isLoading: apiLoading } = useQuery({
    queryKey: ['machines-with-metrics'],
    queryFn: getMachinesWithMetrics,
    refetchInterval: wsConnected ? false : 10000, // Only poll if WS disconnected
    enabled: !wsConnected || wsMachines.length === 0, // Fetch initially or if WS disconnected
  })

  // Use WebSocket data if connected, otherwise API data
  const machines = useMemo(() => {
    if (wsConnected && wsMachines.length > 0) {
      return wsMachines
    }
    return apiMachines
  }, [wsConnected, wsMachines, apiMachines])

  const isLoading = !wsConnected && apiLoading && machines.length === 0

  // Distinct project list for the filter dropdown — from ALL machines so
  // options don't vanish while a filter is active.
  const projectOptions = useMemo(() => {
    const set = new Set<string>()
    let hasNone = false
    for (const m of machines) {
      if (m.project?.trim()) set.add(m.project)
      else hasNone = true
    }
    const sorted = [...set].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
    return { sorted, hasNone }
  }, [machines])

  // Filter machines + alphabetical sort by operator name (case/accent
  // insensitive via localeCompare). Falls back to machine name when the
  // operator name is empty so newly-provisioned PCs still order sensibly.
  const filteredMachines = machines
    .filter((machine: MachineWithMetrics) => {
      const matchesSearch =
        search === '' ||
        machine.name?.toLowerCase().includes(search.toLowerCase()) ||
        machine.operator_name?.toLowerCase().includes(search.toLowerCase()) ||
        machine.location?.toLowerCase().includes(search.toLowerCase()) ||
        machine.project?.toLowerCase().includes(search.toLowerCase())

      const matchesStatus =
        statusFilter === 'all' || machine.status === statusFilter

      const matchesProject =
        projectFilter === 'all' ||
        (projectFilter === '__none__'
          ? !machine.project?.trim()
          : machine.project === projectFilter)

      return matchesSearch && matchesStatus && matchesProject
    })
    .sort((a: MachineWithMetrics, b: MachineWithMetrics) => {
      const na = (a.operator_name || a.name || '').trim()
      const nb = (b.operator_name || b.name || '').trim()
      return na.localeCompare(nb, 'fr', { sensitivity: 'base' })
    })

  // Group by project. Operators without a project (`null`/empty) land
  // in a single bucket rendered last under "Sans projet". Project keys
  // are sorted alphabetically (fr collation) so dispatchers see a
  // stable layout cycle to cycle.
  const PROJECT_NONE = '__none__'
  const groupedByProject = filteredMachines.reduce<Record<string, MachineWithMetrics[]>>(
    (acc, machine) => {
      const key = machine.project && machine.project.trim() ? machine.project : PROJECT_NONE
      if (!acc[key]) acc[key] = []
      acc[key].push(machine)
      return acc
    },
    {},
  )

  const projectKeys = Object.keys(groupedByProject)
    .filter((k) => k !== PROJECT_NONE)
    .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
  if (groupedByProject[PROJECT_NONE]?.length) {
    projectKeys.push(PROJECT_NONE)
  }

  // Count by status
  const onlineCount = machines.filter((m: MachineWithMetrics) => m.status === 'online').length
  const offlineCount = machines.filter((m: MachineWithMetrics) => m.status === 'offline').length
  const warningCount = machines.filter((m: MachineWithMetrics) => m.status === 'warning').length

  const handleCardClick = (machine: MachineWithMetrics) => {
    navigate(`/operators/${machine.machine_id}`)
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div>
          <Title>Temps réel</Title>
          <Text>Supervision des opérateurs en temps réel</Text>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
        <Badge color="green" size="lg">
          {onlineCount} en ligne
        </Badge>
        <Badge color="gray" size="lg">
          {offlineCount} hors ligne
        </Badge>
        {warningCount > 0 && (
          <Badge color="yellow" size="lg">
            {warningCount} attention
          </Badge>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
        <TextInput
          icon={MagnifyingGlassIcon}
          placeholder="Rechercher un opérateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-xs"
        />
        <Select
          value={statusFilter}
          onValueChange={setStatusFilter}
          className="w-full sm:max-w-xs"
        >
          <SelectItem value="all">Tous les statuts</SelectItem>
          <SelectItem value="online">En ligne</SelectItem>
          <SelectItem value="offline">Hors ligne</SelectItem>
          <SelectItem value="warning">Attention</SelectItem>
        </Select>
        <Select
          value={projectFilter}
          onValueChange={setProjectFilter}
          className="w-full sm:max-w-xs"
        >
          <SelectItem value="all">Tous les projets</SelectItem>
          {projectOptions.sorted.map((p) => (
            <SelectItem key={p} value={p}>{p}</SelectItem>
          ))}
          {projectOptions.hasNone && (
            <SelectItem value="__none__">Sans projet</SelectItem>
          )}
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : filteredMachines.length === 0 ? (
        <Text className="text-gray-500">Aucun opérateur trouvé</Text>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {projectKeys.map((projectKey) => {
            const items = groupedByProject[projectKey]
            const label = projectKey === PROJECT_NONE ? 'Sans projet' : projectKey
            return (
              <section key={projectKey}>
                <Flex justifyContent="start" alignItems="baseline" className="gap-2 mb-2 sm:mb-3">
                  <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {label}
                  </Text>
                  <Text className="text-xs text-gray-500">
                    {items.length} opérateur{items.length > 1 ? 's' : ''}
                  </Text>
                </Flex>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {items.map((machine: MachineWithMetrics) => (
                    <OperatorCard
                      key={machine.machine_id}
                      machine={machine}
                      voipState={getVoipStateForMachine(machine.machine_id)}
                      onClick={() => handleCardClick(machine)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
